import React, { Suspense, lazy, Fragment, useState, useEffect, useContext } from "react";
import { Route, Redirect, Switch, useLocation, useHistory } from "react-router-dom";
import Loader from "react-loaders";
import Login from "../../pages/Login/login";
import { AuthContext } from "../../common/index";
import { FaExclamationTriangle } from 'react-icons/fa';

// Immediately rehydrate sessionStorage from sharedSession if available.
(function rehydrateSession() {
  const sharedSession = localStorage.getItem("sharedSession");
  if (sharedSession) {
    const sessionData = JSON.parse(sharedSession);
    console.log("Rehydrating session in new tab:", sessionData);
    Object.keys(sessionData).forEach((key) => {
      sessionStorage.setItem(key, sessionData[key]);
    });
    localStorage.removeItem("sharedSession");
    
  }
})();

// Lazy loaded components
const UserPages = lazy(() => import("../../pages/UserPages"));
const Applications = lazy(() => import("../../pages/Applications"));
const Dashboards = lazy(() => import("../../pages/Dashboards"));
const Widgets = lazy(() => import("../../pages/Widgets"));
const Components = lazy(() => import("../../pages/Components"));
const Charts = lazy(() => import("../../pages/Charts"));
const Forms = lazy(() => import("../../pages/Forms"));
const Tables = lazy(() => import("../../pages/Tables"));
const AddUsers = lazy(() => import("../../pages/CreateCompany"));
const AddPersonnel = lazy(() => import("../../pages/CreatePersonnel"));
const Qrcode = lazy(() => import("../../pages/QRCODE"));
const Points = lazy(() => import("../../pages/Points"));
const Reports = lazy(() => import("../../pages/Reports"));
const Records = lazy(() => import("../../pages/Records"));
const PersonalDashBoard = lazy(() => import("../../pages/PersonalDashBoard"));
const GiftScheme = lazy(() => import("../../pages/Scheme"));
const GiftManagementElectrician = lazy(() => import("../../pages/Giftmanagementelectrician"));
const giftmanagementDealer = lazy(() => import("../../pages/Giftmanagementdealer"));
const UploadPoints = lazy(() => import("../../pages/uploadpoints"));
const RequestForm = lazy(() => import("../../pages/Form"));
const SaleOrders = lazy(() => import("../../pages/SaleOrders"));
const TalleyData = lazy(() => import("../../pages/TalleyData"));
const Sales = lazy(() => import("../../pages/sales"));

const AppMain = ({ onPermissionsChecked }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem("adminId") ? true : false
  );
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
  const location = useLocation();
  const history = useHistory();
  const { permissions, adminMode } = useContext(AuthContext);
  const [hasAnyPermission, setHasAnyPermission] = useState(false);
  const [hasHomePermission, setHasHomePermission] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      setIsAuthenticated(sessionStorage.getItem("adminId") ? true : false);
    };
    window.addEventListener("storage", handleStorageChange);

    if (isAuthenticated && location.pathname === "/Dhanshakthi-AdminLogin") {
      history.replace("/dashboards/crm");
    }
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isAuthenticated, location, history]);

  useEffect(() => {
    if (isAuthenticated && adminMode === "Employee") {
      // Check if employee has home permission (screenId 39)
      const homePermission = permissions["39"]?.is_access;
      setHasHomePermission(homePermission);
      
      // Check if employee has any permission with is_access = true
      const hasPermission = Object.values(permissions).some(
        perm => perm.is_access
      );
      setHasAnyPermission(hasPermission);
      setIsCheckingPermissions(false); // Permissions check complete
      if (onPermissionsChecked) {
        onPermissionsChecked(false); // Notify parent that permissions are checked
      }
    } else {
      setIsCheckingPermissions(false); // No need to check permissions for non-Employee
      if (onPermissionsChecked) {
        onPermissionsChecked(false); // Notify parent
      }
    }
  }, [isAuthenticated, adminMode, permissions, onPermissionsChecked]);

  const handleLogin = () => {
    setIsAuthenticated(true);
    window.dispatchEvent(new Event("storage"));
    history.replace("/dashboards/crm");
  };

  // Check if current route is permitted for employee
  const isRoutePermitted = (path) => {
    if (adminMode !== "Employee") return true;
    
    // If no permissions at all, deny access
    if (!hasAnyPermission) return false;
    
    // Home route is special case
    if (path === "/dashboards/crm") {
      return hasHomePermission;
    }
    

    const hasPermission = (permissions, screenId) => {
      return permissions[screenId]?.is_access === true;
    };
    
    // Check all other routes against permissions
    const routePermissions = {
      "/addcompany/district": ["1"],
      "/addcompany/company": ["2"],
      "/addcompany/group": ["3"],
      "/addcompany/employee": ["4"],
      "/addcompany/changepassword": ["6"],
      "/addcompany/addbanner": ["40"],
      "/addcompany/addannouncement": [""],
      "/addcompany/assigndealers": ["42"],
      "/uploadPoints/electriciantransferpoints": ["5"],
      "/addpersonnel/manager": ["7"],
      "/addpersonnel/representative": ["8"],
      "/addpersonnel/dealer": ["9"],
      "/addpersonnel/salesboy": ["10"],
      "/addpersonnel/electrician": ["11"],
      "/giftscheme/electriciangiftscheme": ["12"],
      "/giftscheme/dealergiftscheme": ["25"],
      "/qrcode/qrcodes": ["13"],
      "/qrcode/qrreports": ["14"],
      "/qrcode/usedqrcodes": ["15"],
      "/qrcode/SearchQrCodeStatus": ["34"],
      "/qrcode/qrcreatedreport": ["43"],
      "/points/electriciantransferpoints": ["16"],
      "/points/salesboytransferpoints": ["17"],
      "/records/electricianReports": ["20"],
      "/records/dealerReports": ["24"],
      "/reports/electricianledgerreports": ["22"],
      "/reports/dealerledgerreports": ["26"],
      "/personaldashboard/electriciandashboard": ["21"],
      "/personaldashboard/dealerdashboard": ["23"],
      "/personaldashboard/salesboydashboard": ["38"],
      "/personaldashboard/dealermonthwisereports": ["47"],
      "/giftmanagementelectirician/reedemgift": ["18"],
      "/giftmanagementelectirician/giftdeveliveredorpending": ["19"],
      "/giftmanagementelectirician/gifthold": ["35"],
      "/giftmanagementelectirician/electriciangiftcancel": ["44"],
      "/giftmanagementDealer/selectgift": ["37"],
      "/giftmanagementDealer/giftdeveliveredorpendingdealer": ["27"],
      "/giftmanagementDealer/dealergifthold": ["36"],
      "/giftmanagementDealer/dealergiftcancel": ["45"],
      "/saleorders/item-management": ["28"],
      "/saleorders/item-positions": ["29"],
      "/saleorders/order-management": ["30"],
      "/saleorders/edit-orders": ["31"],
            "/saleorders/edit-orders": ["48"],
      "/saleorders/in-voice": ["32"],
      "/saleorders/dealer-orderhistory": ["33"],



      "/saleorders/order-creation": ["48"],



      "/dashboards/crm": ["39"], 
      "/addcompany/addannouncement": ["46"],
      "/personaldashboard/salesexcutivedashboard": ["41"],
    };
    // Find which permission group this route belongs to
    const routeKey = Object.keys(routePermissions).find(key => path.startsWith(key));
    if (!routeKey) return false;
    
    const screenIds = routePermissions[routeKey];
    return screenIds.some(screenId => hasPermission(permissions, screenId));
  };

  // Show nothing while checking permissions for Employee
  if (isAuthenticated && adminMode === "Employee" && isCheckingPermissions) {
    return null;
  }

  if (!isAuthenticated && location.pathname !== "/Dhanshakthi-AdminLogin") {
    return <Redirect to="/Dhanshakthi-AdminLogin" />;
  }

  // If employee has no permissions at all
  if (isAuthenticated && adminMode === "Employee" && !hasAnyPermission) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ 
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="text-center p-5 bg-white rounded shadow" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="mb-4" style={{ fontSize: '4rem', color: '#ffc107' }}>
            <FaExclamationTriangle />
          </div>
          <h2 className="text-danger mb-4">
            Permission Denied
          </h2>
          <p className="lead mb-3">
            You don't have permissions to access any screens.
          </p>
          <p className="mb-4">
            Please contact your administrator to request access.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => {
              sessionStorage.clear();
              window.location.href = '/Dhanshakthi-AdminLogin';
            }}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  // If employee doesn't have home permission but has other permissions
  if (isAuthenticated && adminMode === "Employee" && !hasHomePermission) {
    // Allow access if trying to access a permitted route
    if (isRoutePermitted(location.pathname)) {
      // Continue with rendering the requested component
    } else if (location.pathname === "/dashboards/crm") {
      // If trying to access home without permission but has other permissions
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ 
          height: '100vh',
          backgroundColor: '#f8f9fa'
        }}>
          <div className="text-center p-5 bg-white rounded shadow" style={{ maxWidth: '500px', width: '100%' }}>
            <div className="mb-4" style={{ fontSize: '4rem', color: '#ffc107' }}>
              <FaExclamationTriangle />
            </div>
            <h2 className="text-danger mb-4">
              Access Denied
            </h2>
            <p className="lead mb-3">
              You don't have permission to access the dashboard.
            </p>
            <p className="mb-4">
              Please use the navigation menu to access permitted screens.
            </p>
          </div>
        </div>
      );
    } else {
      // For any other route, show permission denied
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ 
          height: '100vh',
          backgroundColor: '#f8f9fa'
        }}>
          <div className="text-center p-5 bg-white rounded shadow" style={{ maxWidth: '500px', width: '100%' }}>
            <div className="mb-4" style={{ fontSize: '4rem', color: '#ffc107' }}>
              <FaExclamationTriangle />
            </div>
            <h2 className="text-danger mb-4">
              Permission Denied
            </h2>
            <p className="lead mb-3">
              You don't have permission to access this screen.
            </p>
            <p className="mb-4">
              Please contact your administrator to request access.
            </p>
          </div>
        </div>
      );
    }
  }

  // If employee tries to access a route they don't have permission for
  if (isAuthenticated && adminMode === "Employee" && !isRoutePermitted(location.pathname)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ 
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="text-center p-5 bg-white rounded shadow" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="mb-4" style={{ fontSize: '4rem', color: '#ffc107' }}>
            <FaExclamationTriangle />
          </div>
          <h2 className="text-danger mb-4">
            Permission Denied
          </h2>
          <p className="lead mb-3">
            You don't have permission to access this screen.
          </p>
          <p className="mb-4">
            Please contact your administrator to request access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <Switch>
        {!isAuthenticated ? (
          <Route path="/Dhanshakthi-AdminLogin" render={() => <Login onLogin={handleLogin} />} />
        ) : (
          <>
            <Suspense
              fallback={
                <div className="loader-container">
                  <div className="loader-container-inner">
                    <div className="text-center">
                      <Loader type="ball-pulse-rise" />
                    </div>
                    <h6 className="mt-5">Please wait....</h6>
                  </div>
                </div>
              }
            >
              <Route path="/components" component={Components} />
              <Route path="/forms" component={Forms} />
              <Route path="/records" component={Records} />
              <Route path="/qrcode" component={Qrcode} />
              <Route path="/points" component={Points} />
              <Route path="/charts" component={Charts} />
              <Route path="/reports" component={Reports} />
              <Route path="/tables" component={Tables} />
              <Route path="/addcompany" component={AddUsers} />
              <Route path="/addpersonnel" component={AddPersonnel} />
              <Route path="/widgets" component={Widgets} />
              <Route path="/pages" component={UserPages} />
              <Route path="/apps" component={Applications} />
              <Route path="/giftscheme" component={GiftScheme} />
              <Route path="/dashboards" component={Dashboards} />
              <Route path="/personaldashboard" component={PersonalDashBoard} />
              <Route path="/giftmanagementelectirician" component={GiftManagementElectrician} />
              <Route path="/giftmanagementDealer" component={giftmanagementDealer} />
              <Route path="/uploadPoints" component={UploadPoints} />
              <Route path="/reportform" component={RequestForm} />          
              <Route path="/saleorders" component={SaleOrders} />   
              <Route path="/talleyData" component={TalleyData} /> 
              <Route path="/sales" component={Sales} /> 
              
            </Suspense>
            <Route exact path="/" render={() => <Redirect to="/dashboards/crm" />} />
          </>
        )}
      </Switch>
    </Fragment>
  );
};

export default AppMain;